import React from 'react';
import { cn } from '@/lib/utils';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';
import { getBackendBaseUrl } from '../../utils/apiBase';

const BACKEND_URL = getBackendBaseUrl();

/** Display name for any archive row tied to an individual (stories, documents, timeline, etc.). */
export function archiveSubjectDisplayName(person) {
  if (!person) return '';
  const parts = [person.given_names, person.surname].filter(Boolean);
  return parts.join(' ').trim();
}

export function archiveSubjectInitials(person) {
  if (!person) return '?';
  const g = (person.given_names || '').trim();
  const s = (person.surname || '').trim();
  const first = g.charAt(0);
  const second = s.charAt(0) || g.split(/\s+/).filter(Boolean)[1]?.charAt(0) || '';
  const pair = `${first}${second}`.toUpperCase();
  return pair || '?';
}

/** e.g. "Adam's" or "James'" for section titles (first given token only). */
export function firstGivenPossessiveLabel(person) {
  const raw = (person?.given_names || '').trim().split(/\s+/)[0];
  if (!raw) return '';
  return /s$/i.test(raw) ? `${raw}'` : `${raw}'s`;
}

/**
 * Compact person anchor for archival UIs (not a social “profile header”).
 * @param {{ given_names?: string, surname?: string, profile_image_url?: string|null }} person
 * @param {'default'|'compact'|'rail'} variant — default: story surfaces; compact: dense cards; rail: quiet sidebar
 */
export function ArchiveSubjectIdentity({ person, caption, variant = 'default', className }) {
  if (!person) return null;
  const name = archiveSubjectDisplayName(person);
  if (!name) return null;

  const initials = archiveSubjectInitials(person);
  const src = person.profile_image_url ? resolveAssetUrl(BACKEND_URL, person.profile_image_url) : null;

  const ring =
    variant === 'rail'
      ? 'border-[hsl(38_14%_88%)] bg-[hsl(42_28%_99%)] ring-0'
      : 'border-[hsl(38_16%_86%)] bg-[hsl(42_28%_98%)] ring-1 ring-[#c9a961]/12';

  const avatar =
    variant === 'compact'
      ? 'h-7 w-7 text-[9px]'
      : variant === 'rail'
        ? 'h-6 w-6 text-[8px]'
        : 'h-9 w-9 text-[11px]';

  const nameClass =
    variant === 'rail'
      ? 'text-[11px] font-normal leading-snug text-[hsl(32_10%_44%)]'
      : variant === 'compact'
        ? 'text-xs font-medium leading-snug text-[hsl(24_12%_22%)]'
        : 'text-sm font-medium leading-snug text-[hsl(24_12%_22%)]';

  const captionClass =
    variant === 'rail' ? 'text-[10px] text-[hsl(32_10%_50%)]' : 'text-[11px] text-muted-foreground';

  return (
    <div
      className={cn('flex min-w-0 items-center gap-2', variant === 'default' && 'gap-2.5', className)}
      data-testid="archive-subject-identity"
    >
      <div
        className={cn(
          'flex shrink-0 items-center justify-center overflow-hidden rounded-full font-sans font-semibold uppercase tracking-wide text-[#5c4a32]',
          ring,
          avatar
        )}
      >
        {src ? (
          <img src={src} alt="" className="h-full w-full object-cover" loading="lazy" />
        ) : (
          initials
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className={cn('truncate', nameClass)} title={name}>
          {name}
        </p>
        {caption ? <p className={cn('mt-0.5 truncate', captionClass)}>{caption}</p> : null}
      </div>
    </div>
  );
}
